// ============================================================================
// 《천하장기》 X 《천천장기(天天象棋)》 완전 흡수 & 초월 컨텐츠 엔진 (TianTianContentEngine)
// 1. 하수가 고수를 잡는 6대 비급 아이템 중 신규 3종:
//    - ⏳ 회광반조(한수 무르기 / 悔棋卡)
//    - 👁️ 천안통(고수 노림수·살수 투시경 & 실시간 형세판단 / 局势分析·杀招透视)
//    - 📜 어전 강등방어 면책패 & 고수격파 승점 2배 부적 (保级卡·双倍积分卡)
// 2. 최고급 원목 장기판 6종 & 최고급 수제 장기알 6종 상품화 및 품격도(装扮值) 시스템
// 3. 잔국도전(残局闯关) — 고전 명국 사활·외통 묘수풀이 캠페인 스테이지 엔진
// 4. 천하 명인 4대 랭킹전(급단 랭킹 / 하극상 연승 랭킹 / 잔국 최단수 랭킹 / 명문 기원 대항전)
// ============================================================================

import {
  BoardGrid,
  Move,
  Pos,
  Side,
  PIECE_NAMES_KO,
  applyMoveOnClone,
  createEmptyBoard,
  formatKoreanNotation,
  getAllLegalMoves,
  isKingInCheck
} from './JanggiRules.js';
import {
  analyzeTopMoves,
  cpToWinRatePercent,
  evaluateBoardCp
} from './JanggiAI.js';

// ============================================================================
// 1. 최고급 장기판(Board Skin) 6종 & 최고급 장기알(Piece Skin) 6종 상품화 카탈로그
// ============================================================================
export type BoardSkinId = string;
export type PieceSkinId = string;

export interface BoardSkinItem {
  id: string;
  name: string;
  subtitle: string;
  woodOrigin: string;
  rarity: string;
  imageUrl: string;
  textureUrl: string;
  surfaceTint: string;
  gridLineColor: string;
  coordinateColor: string;
  frameBorderColor: string;
  priceGems: number;
  priceCoins: number;
  prestigeScore: number; // 천천장기 '装扮值(장식 품격 점수)' 개념 초월
}

export type BoardSkinSpec = BoardSkinItem;

export interface PieceSkinItem {
  id: string;
  name: string;
  subtitle: string;
  materialLabel: string;
  rarity: string;
  previewImageUrl: string;
  choBgGradient: [string, string, string];
  hanBgGradient: [string, string, string];
  choBorderColor: string;
  hanBorderColor: string;
  innerRingColor: string;
  choTextColor: string;
  hanTextColor: string;
  choTextShadow: string;
  hanTextShadow: string;
  hasMetallicStuds?: boolean;
  priceGems: number;
  priceCoins: number;
  prestigeScore: number;
}

export type PieceSkinSpec = PieceSkinItem;

export const BOARD_SKIN_CATALOG: BoardSkinItem[] = [
  {
    id: 'board_zelkova',
    name: '수령 300년 금사 느티나무반',
    subtitle: '치밀한 나이테와 따뜻한 황갈색 결이 살아있는 정통 느티나무 대국반입니다.',
    woodOrigin: '한국 안동 수령 300년 금사 느티나무 통원목',
    rarity: '전통 명목',
    imageUrl: '/assets/images/astra/board_zelkova_hd.png',
    textureUrl: '/assets/images/astra/board_zelkova_hd.png',
    surfaceTint: 'rgba(218, 165, 92, 0.14)',
    gridLineColor: '#2d1a0b',
    coordinateColor: 'rgba(45, 26, 11, 0.78)',
    frameBorderColor: '#3a220f',
    priceGems: 0,
    priceCoins: 0,
    prestigeScore: 100
  },
  {
    id: 'board_royal_pine',
    name: '천년 황장목(黃腸木) 어전 대국반',
    subtitle: '조선 왕실 궁궐 건축에만 쓰이던 천년 황장목 원판에 황동 용문 모서리 장식을 더한 명품반입니다.',
    woodOrigin: '울진 소광리 천년 금강송 황장목 & 황동 용문장식',
    rarity: '궁중 진상품',
    imageUrl: '/assets/images/astra/board_royal_pine_hd.png',
    textureUrl: '/assets/images/astra/board_royal_pine_hd.png',
    surfaceTint: 'rgba(245, 195, 105, 0.12)',
    gridLineColor: '#241305',
    coordinateColor: 'rgba(36, 19, 5, 0.82)',
    frameBorderColor: '#b47b28',
    priceGems: 80,
    priceCoins: 3000,
    prestigeScore: 450
  },
  {
    id: 'board_rosewood_lacquer',
    name: '최고급 자단목(紫檀木) 나전칠기반',
    subtitle: '깊고 중후한 적갈색 자단목 위에 영롱한 천연 통영 자개(나전칠기)로 학과 구름을 수놓은 국보급 장기판입니다.',
    woodOrigin: '인도산 최고급 소엽자단(小葉紫檀) & 통영 천연 자개',
    rarity: '국보급 공예',
    imageUrl: '/assets/images/astra/board_rosewood_lacquer_hd.png',
    textureUrl: '/assets/images/astra/board_rosewood_lacquer_hd.png',
    surfaceTint: 'rgba(45, 12, 12, 0.22)',
    gridLineColor: '#fde68a',
    coordinateColor: 'rgba(253, 230, 138, 0.88)',
    frameBorderColor: '#e2af42',
    priceGems: 140,
    priceCoins: 6000,
    prestigeScore: 880
  },
  {
    id: 'board_jade_celadon',
    name: '고려 비취 청자(靑磁) 옥돌반',
    subtitle: '눈의 피로를 씻어주는 은은한 비취빛 고려청자 유약과 옥돌 질감으로 시니어 대국수들에게 가장 사랑받는 명반입니다.',
    woodOrigin: '강진 비취 청자 유약 & 천연 비취옥 상감',
    rarity: '국보급 공예',
    imageUrl: '/assets/images/astra/board_jade_hd.png',
    textureUrl: '/assets/images/astra/board_jade_hd.png',
    surfaceTint: 'rgba(180, 225, 200, 0.14)',
    gridLineColor: '#132a1e',
    coordinateColor: 'rgba(19, 42, 30, 0.82)',
    frameBorderColor: '#2d6a4f',
    priceGems: 150,
    priceCoins: 6500,
    prestigeScore: 720
  },
  {
    id: 'board_ebony_gold',
    name: '어전 흑단(黑檀) 순금 상감반',
    subtitle: '물에 가라앉을 만큼 단단한 최고급 흑단목에 24K 금사로 격자선을 상감 처리하여 기물이 눈부시게 돋보입니다.',
    woodOrigin: '실론 최상급 흑단목 & 24K 순금 입사(入絲)',
    rarity: '어전 지존',
    imageUrl: '/assets/images/astra/board_ebony_hd.png',
    textureUrl: '/assets/images/astra/board_ebony_hd.png',
    surfaceTint: 'rgba(15, 18, 24, 0.32)',
    gridLineColor: '#e5c068',
    coordinateColor: 'rgba(229, 192, 104, 0.85)',
    frameBorderColor: '#eab308',
    priceGems: 200,
    priceCoins: 9000,
    prestigeScore: 1200
  },
  {
    id: 'board_imperial_dragon',
    name: '천하제패 황룡 백옥(白玉) 어좌반',
    subtitle: '황금 용 조각과 백옥 판면이 조화를 이룬 대국수(9단) 전용 최고 권위의 어좌 대국반입니다.',
    woodOrigin: '황실 어전 백옥 & 99.9% 황금 용조각 테두리',
    rarity: '천하 유일',
    imageUrl: '/assets/images/astra/board_royal_pine_hd.png',
    textureUrl: '/assets/images/astra/board_royal_pine_hd.png',
    surfaceTint: 'rgba(255, 248, 220, 0.22)',
    gridLineColor: '#3b1d06',
    coordinateColor: 'rgba(59, 29, 6, 0.85)',
    frameBorderColor: '#ffd700',
    priceGems: 300,
    priceCoins: 15000,
    prestigeScore: 2500
  }
];

export const PIECE_SKIN_CATALOG: PieceSkinItem[] = [
  {
    id: 'piece_jujube_classic',
    name: '전통 대추나무 음각석',
    subtitle: '손에 감기는 따뜻한 목질감과 선명한 궁중 궁서체 음각이 돋보이는 정통 장기알입니다.',
    materialLabel: '경산 벼락대추나무 자연건조 통원목',
    rarity: '전통 명목',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#fffdf7', '#f6dfb8', '#c89b5c'],
    hanBgGradient: ['#fffdf7', '#f6dfb8', '#c89b5c'],
    choBorderColor: '#0c4a6e',
    hanBorderColor: '#7f1d1d',
    innerRingColor: 'rgba(146, 64, 14, 0.45)',
    choTextColor: '#075985',
    hanTextColor: '#b91c1c',
    choTextShadow: 'rgba(255, 255, 255, 0.85)',
    hanTextShadow: 'rgba(255, 255, 255, 0.85)',
    priceGems: 0,
    priceCoins: 0,
    prestigeScore: 100
  },
  {
    id: 'piece_boxwood_master',
    name: '최고급 회양목(黃楊木) 명인 수제알',
    subtitle: '상아처럼 매끄럽고 황금빛이 감도는 최고급 회양목을 장인이 하나하나 깎아 이중 금테를 두른 명품 장기알입니다.',
    materialLabel: '해남 100년산 최고급 회양목 & 송진 옻칠 마감',
    rarity: '궁중 진상품',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#fffef5', '#fde68a', '#d97706'],
    hanBgGradient: ['#fffef5', '#fde68a', '#d97706'],
    choBorderColor: '#b45309',
    hanBorderColor: '#991b1b',
    innerRingColor: 'rgba(217, 119, 6, 0.75)',
    choTextColor: '#0369a1',
    hanTextColor: '#dc2626',
    choTextShadow: 'rgba(255, 255, 255, 0.95)',
    hanTextShadow: 'rgba(255, 255, 255, 0.95)',
    priceGems: 75,
    priceCoins: 2800,
    prestigeScore: 420
  },
  {
    id: 'piece_jade_royal',
    name: '황실 비취·백옥(翡翠·白玉) 보석알',
    subtitle: '초(楚)와 한(漢)의 기물을 영롱한 비취 백옥으로 세공하여 착수할 때마다 맑고 고아한 옥빛 광채를 뿜어냅니다.',
    materialLabel: '천연 비취옥 & 호탄 백옥 수공예 연마',
    rarity: '국보급 공예',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#ecfdf5', '#a7f3d0', '#059669'],
    hanBgGradient: ['#fff1f2', '#fecdd3', '#e11d48'],
    choBorderColor: '#047857',
    hanBorderColor: '#9f1239',
    innerRingColor: 'rgba(255, 255, 255, 0.75)',
    choTextColor: '#064e3b',
    hanTextColor: '#881337',
    choTextShadow: 'rgba(255, 255, 255, 0.9)',
    hanTextShadow: 'rgba(255, 255, 255, 0.9)',
    priceGems: 140,
    priceCoins: 5500,
    prestigeScore: 850
  },
  {
    id: 'piece_obsidian_gold',
    name: '순금·흑요석(純金·黑曜石) 어전 용문알',
    subtitle: '중후한 칠흑빛 흑요석 바디 위에 24K 순금과 홍옥으로 한자를 양각하여 압도적인 카리스마를 자랑합니다.',
    materialLabel: '백두산 천연 흑요석 & 24K 순금박 상감',
    rarity: '어전 지존',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#334155', '#1e293b', '#090d16'],
    hanBgGradient: ['#3b1d2e', '#1f1018', '#0c0508'],
    choBorderColor: '#eab308',
    hanBorderColor: '#f59e0b',
    innerRingColor: 'rgba(250, 204, 21, 0.8)',
    choTextColor: '#38bdf8',
    hanTextColor: '#fb7185',
    choTextShadow: 'rgba(0, 0, 0, 0.9)',
    hanTextShadow: 'rgba(0, 0, 0, 0.9)',
    hasMetallicStuds: true,
    priceGems: 220,
    priceCoins: 9500,
    prestigeScore: 1350
  },
  {
    id: 'piece_mother_of_pearl',
    name: '조선 국보 나전칠기(螺鈿漆器) 자개알',
    subtitle: '깊은 옻칠 광택 위에 빛의 각도에 따라 오색찬란하게 빛나는 천연 자개 문양을 상감한 예술품입니다.',
    materialLabel: '원주 생옻칠 흑단 & 통영 오색 전복 자개',
    rarity: '어전 지존',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#312e81', '#1e1b4b', '#0f0e26'],
    hanBgGradient: ['#4a1d46', '#2e102b', '#140613'],
    choBorderColor: '#22d3ee',
    hanBorderColor: '#e879f9',
    innerRingColor: 'rgba(103, 232, 249, 0.65)',
    choTextColor: '#67e8f9',
    hanTextColor: '#f472b6',
    choTextShadow: 'rgba(15, 23, 42, 0.9)',
    hanTextShadow: 'rgba(15, 23, 42, 0.9)',
    priceGems: 180,
    priceCoins: 7500,
    prestigeScore: 1500
  },
  {
    id: 'piece_war_drum',
    name: '삼국 전고(三國戰鼓) 혈투 무신알',
    subtitle: '천천장기의 명물 삼국전고(三国战鼓) 기물을 초월 재해석! 테두리에 8개의 황금 전고 징(Stud)이 박혀 타격감을 배가시킵니다.',
    materialLabel: '단조 황동 리벳(징) & 적단목 전고(戰鼓) 조각',
    rarity: '천하 유일',
    previewImageUrl: '/assets/images/astra/piece_skin_showcase_hd.png',
    choBgGradient: ['#fef3c7', '#d97706', '#78350f'],
    hanBgGradient: ['#fee2e2', '#dc2626', '#7f1d1d'],
    choBorderColor: '#fbbf24',
    hanBorderColor: '#f87171',
    innerRingColor: 'rgba(254, 240, 138, 0.85)',
    choTextColor: '#1e3a8a',
    hanTextColor: '#fef08a',
    choTextShadow: 'rgba(254, 243, 199, 0.85)',
    hanTextShadow: 'rgba(0, 0, 0, 0.85)',
    hasMetallicStuds: true,
    priceGems: 260,
    priceCoins: 12000,
    prestigeScore: 2200
  }
];

const BOARD_SKIN_ID_ALIASES: Record<string, string> = {
  board_rosewood: 'board_rosewood_lacquer',
  board_jade_palace: 'board_jade_celadon',
  board_three_kingdoms_stone: 'board_imperial_dragon',
  classic_kaya: 'board_zelkova',
  royal_pine: 'board_royal_pine',
  red_sandalwood: 'board_rosewood_lacquer',
  imperial_jade: 'board_jade_celadon',
  royal_ebony: 'board_ebony_gold'
};

export function getBoardSkinById(id: string): BoardSkinItem {
  const canonical = BOARD_SKIN_ID_ALIASES[id] || id;
  return BOARD_SKIN_CATALOG.find(b => b.id === canonical) || BOARD_SKIN_CATALOG[0];
}

export function getPieceSkinById(id: string): PieceSkinItem {
  return PIECE_SKIN_CATALOG.find(p => p.id === id) || PIECE_SKIN_CATALOG[1];
}

// ============================================================================
// 2. 천안통(天眼通) — 상대 고수의 다음 2수 살수·노림수 투시 & 실시간 형세판단 엔진
// ============================================================================
export interface EnemyIntentVisionResult {
  myWinRatePercent: number;
  winRateChoPercent: number;
  enemyWinRatePercent: number;
  primaryEnemyThreat: Move | null;
  secondaryEnemyThreat: Move | null;
  enemyFollowUpStrike: Move | null;
  targetFriendlyPiecePos: Pos | null;
  headline: string;
  detail: string;
  threatStep1Desc: string;
  threatStep2Desc: string;
}

export type EnemyIntentVision = EnemyIntentVisionResult;

export function computeEnemyIntentVision(board: BoardGrid, mySide: Side): EnemyIntentVisionResult {
  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const myCp = evaluateBoardCp(board, mySide);
  const myWinRatePercent = cpToWinRatePercent(myCp);
  const enemyWinRatePercent = Math.round((100 - myWinRatePercent) * 10) / 10;

  const enemyTopEvals = analyzeTopMoves(board, enemySide, 2, 3);
  const primary = enemyTopEvals[0]?.move || null;
  const secondary = enemyTopEvals[1]?.move || null;
  const followUp = enemyTopEvals[0]?.bestFollowUpMove || null;

  if (!primary) {
    return {
      myWinRatePercent,
      winRateChoPercent: myWinRatePercent,
      enemyWinRatePercent,
      primaryEnemyThreat: null,
      secondaryEnemyThreat: null,
      enemyFollowUpStrike: null,
      targetFriendlyPiecePos: null,
      headline: `👁️ [천안통 형세투시] 현재 내 승률 ${myWinRatePercent}% — 상대의 뚜렷한 살수가 없습니다.`,
      detail: `안정적인 판세입니다. 중앙 기물을 전진시켜 주도권을 유지하세요.`,
      threatStep1Desc: '① 적 1차 노림수: 뚜렷한 위협 없음',
      threatStep2Desc: '② 적 후속 연계: 중앙 기물 정비 예상'
    };
  }

  const pNotation = formatKoreanNotation(primary.piece, primary.from, primary.to, primary.captured);
  const fNotation = followUp
    ? formatKoreanNotation(followUp.piece, followUp.from, followUp.to, followUp.captured)
    : '중앙 기물 압박';

  const targetDesc = primary.captured
    ? `내 '${PIECE_NAMES_KO[primary.captured.type][mySide]}' 포획 노림!`
    : primary.gaveCheck
      ? `매서운 장군(將軍) 공세!`
      : `핵심 길목 침투`;

  const targetFriendlyPiecePos = primary.captured
    ? primary.to
    : followUp?.captured
      ? followUp.to
      : null;

  return {
    myWinRatePercent,
    winRateChoPercent: myWinRatePercent,
    enemyWinRatePercent,
    primaryEnemyThreat: primary,
    secondaryEnemyThreat: secondary,
    enemyFollowUpStrike: followUp,
    targetFriendlyPiecePos,
    headline: `👁️ [천안통 고수 노림수 포착!] 상대는 다음 수에 [${pNotation}] (${targetDesc})을(를) 노리고 있습니다!`,
    detail: `현재 형세: 내 승률 ${myWinRatePercent}% vs 상대 ${enemyWinRatePercent}%. 보드의 보라·진홍색 살수 궤적을 미리 차단하세요!`,
    threatStep1Desc: `① 적 1차 살초(보라선): ${pNotation} (${targetDesc})`,
    threatStep2Desc: `② 적 2차 후속타(적색점선): ${fNotation}`
  };
}

// ============================================================================
// 3. 잔국도전(残局闯关) — 고전 명국 사활·묘수풀이 캠페인 스테이지 데이터
// ============================================================================
export interface EndgamePuzzleStage {
  stageNumber: number;
  id: string;
  title: string;
  subtitle: string;
  eraSubtitle: string;
  difficultyStars: number;
  maxMovesToWin: number;
  description: string;
  hintText: string;
  rewardCoins: number;
  rewardGems: number;
  setupBoard: () => BoardGrid;
  winningMoveHint: { from: Pos; to: Pos };
}

export const ENDGAME_PUZZLE_STAGES: EndgamePuzzleStage[] = [
  {
    stageNumber: 1,
    id: 'puzzle_01',
    title: '칠성취회(七星聚會)',
    subtitle: '쌍차귀문 외통 일섬',
    eraSubtitle: '입문 사활 · 1수 외통 또는 대차 결승타',
    difficultyStars: 1,
    maxMovesToWin: 5,
    description: '한(漢)의 궁성이 얇아진 틈을 타, 초(楚)의 선봉 차(車)로 단숨에 상대 궁성을 꿰뚫어 제압하세요!',
    hintText: '💡 비결: 우측에 대기 중인 초 차(車)를 적 궁성 깊숙이 전진시켜 장군을 부르세요!',
    rewardCoins: 1500,
    rewardGems: 40,
    winningMoveHint: { from: { row: 3, col: 7 }, to: { row: 1, col: 7 } },
    setupBoard: () => {
      const b = createEmptyBoard();
      b[1][4] = { id: 'han_king', side: 'han', type: 'king' };
      b[0][3] = { id: 'han_sa_1', side: 'han', type: 'sa' };
      b[2][3] = { id: 'han_jol_1', side: 'han', type: 'jol' };

      b[8][4] = { id: 'cho_king', side: 'cho', type: 'king' };
      b[8][3] = { id: 'cho_sa_1', side: 'cho', type: 'sa' };
      b[3][7] = { id: 'cho_cha_r', side: 'cho', type: 'cha' };
      b[1][1] = { id: 'cho_cha_l', side: 'cho', type: 'cha' };
      b[4][4] = { id: 'cho_po_c', side: 'cho', type: 'po' };
      return b;
    }
  },
  {
    stageNumber: 2,
    id: 'puzzle_02',
    title: '천리독행(千里獨行)',
    subtitle: '중포천궁 포·차 협공',
    eraSubtitle: '초급 사활 · 포 다리를 활용한 궁성 봉쇄',
    difficultyStars: 2,
    maxMovesToWin: 7,
    description: '중앙의 포(包)와 차(車)를 연계하여 한(漢)의 수비진을 무너뜨리고 항복을 받아내세요!',
    hintText: '💡 비결: 초 차(車)를 적 궁성 옆구리로 침투시켜 포(包)의 사거리를 여세요!',
    rewardCoins: 2000,
    rewardGems: 50,
    winningMoveHint: { from: { row: 4, col: 2 }, to: { row: 1, col: 2 } },
    setupBoard: () => {
      const b = createEmptyBoard();
      b[1][4] = { id: 'han_king', side: 'han', type: 'king' };
      b[0][5] = { id: 'han_sa_r', side: 'han', type: 'sa' };
      b[3][4] = { id: 'han_ma_c', side: 'han', type: 'ma' };

      b[9][4] = { id: 'cho_king', side: 'cho', type: 'king' };
      b[5][4] = { id: 'cho_po_1', side: 'cho', type: 'po' };
      b[4][2] = { id: 'cho_cha_1', side: 'cho', type: 'cha' };
      b[2][6] = { id: 'cho_ma_1', side: 'cho', type: 'ma' };
      return b;
    }
  },
  {
    stageNumber: 3,
    id: 'puzzle_03',
    title: '포마쟁웅(包馬爭雄)',
    subtitle: '상수절명 비상의 습격',
    eraSubtitle: '중급 묘수 · 상(象)과 차(車)의 환상 복병전',
    difficultyStars: 3,
    maxMovesToWin: 9,
    description: '상대방이 미처 보지 못한 상(象)의 대각선 도약으로 적의 주력 대차(車)를 낚아채거나 궁성을 타격하세요!',
    hintText: '💡 비결: 상수(象手)를 켜서 상(象)의 착지점을 확인하거나 적의 무방비 차(車)를 타격하세요!',
    rewardCoins: 2500,
    rewardGems: 65,
    winningMoveHint: { from: { row: 5, col: 2 }, to: { row: 2, col: 4 } },
    setupBoard: () => {
      const b = createEmptyBoard();
      b[0][4] = { id: 'han_king', side: 'han', type: 'king' };
      b[2][4] = { id: 'han_cha_1', side: 'han', type: 'cha' };
      b[1][5] = { id: 'han_sa_1', side: 'han', type: 'sa' };

      b[9][4] = { id: 'cho_king', side: 'cho', type: 'king' };
      b[5][2] = { id: 'cho_sang_1', side: 'cho', type: 'sang' };
      b[4][7] = { id: 'cho_cha_1', side: 'cho', type: 'cha' };
      b[6][4] = { id: 'cho_po_1', side: 'cho', type: 'po' };
      return b;
    }
  },
  {
    stageNumber: 4,
    id: 'puzzle_04',
    title: '쌍차협공(雙車夾攻)',
    subtitle: '마후포 천고의 정석 살수',
    eraSubtitle: '고급 사활 · 마(馬)로 묶고 포(包)로 쏘다',
    difficultyStars: 4,
    maxMovesToWin: 10,
    description: '장기 최고의 살수인 마후포(馬後包) 진형을 완성하여 적장의 퇴로를 완벽히 봉쇄하세요!',
    hintText: '💡 비결: 마(馬)를 적 궁성 턱밑으로 도약시켜 장군과 동시에 포 다리를 만드세요!',
    rewardCoins: 3500,
    rewardGems: 85,
    winningMoveHint: { from: { row: 4, col: 3 }, to: { row: 2, col: 2 } },
    setupBoard: () => {
      const b = createEmptyBoard();
      b[1][4] = { id: 'han_king', side: 'han', type: 'king' };
      b[0][3] = { id: 'han_sa_1', side: 'han', type: 'sa' };
      b[0][5] = { id: 'han_sa_2', side: 'han', type: 'sa' };

      b[9][4] = { id: 'cho_king', side: 'cho', type: 'king' };
      b[4][3] = { id: 'cho_ma_1', side: 'cho', type: 'ma' };
      b[1][1] = { id: 'cho_po_1', side: 'cho', type: 'po' };
      b[3][6] = { id: 'cho_cha_1', side: 'cho', type: 'cha' };
      return b;
    }
  },
  {
    stageNumber: 5,
    id: 'puzzle_05',
    title: '어전진공(御前進攻)',
    subtitle: '한신배수진 절체절명 대역전',
    eraSubtitle: '명인 사활 · 내 궁성의 위기를 넘기고 적의 대차를 격파하라',
    difficultyStars: 5,
    maxMovesToWin: 12,
    description: '적의 맹공 속에서도 냉정하게 훈수(訓手)와 수읽기를 발휘하여 적의 공격수 차(車)를 역공으로 잡아내세요!',
    hintText: '💡 비결: 적의 위협 기물을 먼저 선제 타격하여 장군으로 되받아치세요!',
    rewardCoins: 5000,
    rewardGems: 120,
    winningMoveHint: { from: { row: 5, col: 5 }, to: { row: 2, col: 5 } },
    setupBoard: () => {
      const b = createEmptyBoard();
      b[1][4] = { id: 'han_king', side: 'han', type: 'king' };
      b[2][5] = { id: 'han_cha_danger', side: 'han', type: 'cha' };
      b[0][3] = { id: 'han_sa_1', side: 'han', type: 'sa' };

      b[8][4] = { id: 'cho_king', side: 'cho', type: 'king' };
      b[8][5] = { id: 'cho_sa_1', side: 'cho', type: 'sa' };
      b[5][5] = { id: 'cho_cha_hero', side: 'cho', type: 'cha' };
      b[4][2] = { id: 'cho_ma_hero', side: 'cho', type: 'ma' };
      return b;
    }
  }
];

export function validateEndgamePuzzleSolution(
  stageOrId: EndgamePuzzleStage | string,
  board: BoardGrid,
  candidateMove: { from: Pos; to: Pos },
  side: Side = 'cho'
): { isCorrect: boolean; isCheckmate: boolean; reason: string } {
  const stage =
    typeof stageOrId === 'string'
      ? ENDGAME_PUZZLE_STAGES.find(s => s.id === stageOrId) || ENDGAME_PUZZLE_STAGES[0]
      : stageOrId;

  const legalMoves = getAllLegalMoves(board, side);
  const matchedLegal = legalMoves.find(
    m =>
      m.from.row === candidateMove.from.row &&
      m.from.col === candidateMove.from.col &&
      m.to.row === candidateMove.to.row &&
      m.to.col === candidateMove.to.col
  );

  if (!matchedLegal) {
    return { isCorrect: false, isCheckmate: false, reason: '합법적인 행마가 아닙니다.' };
  }

  // 1) 정석 힌트 좌표와 일치하면 즉시 정답 인정
  if (
    stage.winningMoveHint.from.row === candidateMove.from.row &&
    stage.winningMoveHint.from.col === candidateMove.from.col &&
    stage.winningMoveHint.to.row === candidateMove.to.row &&
    stage.winningMoveHint.to.col === candidateMove.to.col
  ) {
    return { isCorrect: true, isCheckmate: Boolean(matchedLegal.gaveCheck), reason: '정석 해법 수순입니다!' };
  }

  // 2) 다중 해법 검증: 외통수를 완성하거나 엔진 최선수 대비 동급의 결정타(+400cp 이상 우세/최선수 근접)인 경우도 정답 인정!
  const enemySide: Side = side === 'cho' ? 'han' : 'cho';
  const { nextBoard } = applyMoveOnClone(board, candidateMove.from, candidateMove.to);
  const oppMoves = getAllLegalMoves(nextBoard, enemySide);
  if (oppMoves.length === 0 && isKingInCheck(nextBoard, enemySide)) {
    return { isCorrect: true, isCheckmate: true, reason: '멋진 외통수로 국면을 해결했습니다!' };
  }

  const topEvals = analyzeTopMoves(board, side, 3, 6);
  const bestEval = topEvals[0];
  const myEval = topEvals.find(
    e =>
      e.move.from.row === candidateMove.from.row &&
      e.move.from.col === candidateMove.from.col &&
      e.move.to.row === candidateMove.to.row &&
      e.move.to.col === candidateMove.to.col
  );

  if (bestEval && myEval && !myEval.isSelfTrap && myEval.scoreCp >= bestEval.scoreCp - 90) {
    return { isCorrect: true, isCheckmate: false, reason: '엔진이 인정한 또 다른 승리 묘수입니다!' };
  }

  return {
    isCorrect: false,
    isCheckmate: false,
    reason: '더 날카로운 묘수가 있습니다. 궁성 장군 길이나 주력 기물 타격 수를 다시 찾아보세요.'
  };
}

// ============================================================================
// 4. 천하 명인 4대 랭킹전 (급·단 랭킹 / 하극상 격파 랭킹 / 잔국도전 랭킹 / 명문 기원 랭킹)
// ============================================================================
export type LeaderboardCategory = 'tier' | 'upset' | 'puzzle' | 'guild';

export interface RankingEntry {
  rank: number;
  nickname: string;
  tierLabel: string;
  titleBadge: string;
  guildName: string;
  rating: number;
  winRatePercent: number;
  upsetWins: number;
  puzzlesCleared: number;
  prestigeScore: number;
  scoreValue: string;
  subDetail: string;
  avatarUrl: string;
  isMe?: boolean;
  isSampleShowcase?: boolean;
  aiMasterTierIndex?: number;
}

const BASE_LEADERBOARD_ENTRIES: RankingEntry[] = [
  {
    rank: 1,
    nickname: '어전백학국수',
    tierLabel: '9단',
    titleBadge: '👑 제48대 천하명인',
    guildName: '황실 어전기원',
    rating: 3180,
    winRatePercent: 84.2,
    upsetWins: 64,
    puzzlesCleared: 5,
    prestigeScore: 7600,
    scoreValue: '3,180 RP (승률 84.2%)',
    subDetail: '천하명인 타이틀 4연패 · 품격 7,600점',
    avatarUrl: '/assets/images/astra/master_scholar_ai.png',
    isSampleShowcase: true,
    aiMasterTierIndex: 26
  },
  {
    rank: 2,
    nickname: '종로맹호대사범',
    tierLabel: '8단',
    titleBadge: '⚡ 하극상 격파왕',
    guildName: '종로 국수기원',
    rating: 2940,
    winRatePercent: 78.5,
    upsetWins: 92,
    puzzlesCleared: 5,
    prestigeScore: 6200,
    scoreValue: '2,940 RP (승률 78.5%)',
    subDetail: '하극상 92승 · 품격 6,200점',
    avatarUrl: '/assets/images/astra/master_tiger_dan.png',
    isSampleShowcase: true,
    aiMasterTierIndex: 25
  },
  {
    rank: 3,
    nickname: '자운당송여류명인',
    tierLabel: '7단',
    titleBadge: '🏯 잔국 비경 지존',
    guildName: '자운당 명인회',
    rating: 2810,
    winRatePercent: 76.1,
    upsetWins: 78,
    puzzlesCleared: 5,
    prestigeScore: 6850,
    scoreValue: '2,810 RP (승률 76.1%)',
    subDetail: '잔국 최단수 돌파 · 품격 6,850점',
    avatarUrl: '/assets/images/astra/master_queen.png',
    isSampleShowcase: true,
    aiMasterTierIndex: 24
  },
  {
    rank: 4,
    nickname: '관악산신선9단',
    tierLabel: '9단',
    titleBadge: '🐉 국수전 준우승',
    guildName: '관악 기우회',
    rating: 3090,
    winRatePercent: 81.0,
    upsetWins: 55,
    puzzlesCleared: 5,
    prestigeScore: 5900,
    scoreValue: '3,090 RP (승률 81.0%)',
    subDetail: '천안통·마후포 달인 · 품격 5,900점',
    avatarUrl: '/assets/images/astra/master_scholar_ai.png',
    isSampleShowcase: true,
    aiMasterTierIndex: 26
  },
  {
    rank: 5,
    nickname: '달구벌쌍차달인',
    tierLabel: '5단',
    titleBadge: '🔥 15연승 파죽지세',
    guildName: '달구벌 달인당',
    rating: 2590,
    winRatePercent: 71.4,
    upsetWins: 83,
    puzzlesCleared: 4,
    prestigeScore: 4800,
    scoreValue: '2,590 RP (승률 71.4%)',
    subDetail: '양귀마 속공 장인 · 품격 4,800점',
    avatarUrl: '/assets/images/astra/master_player.png',
    isSampleShowcase: true,
    aiMasterTierIndex: 22
  }
];

export function getLeaderboardByCategory(
  category: LeaderboardCategory,
  myNickname = '종로대국수',
  myTierLabel = '9급',
  myRating = 1515,
  myUpsetWins = 3,
  myPuzzlesCleared = 0,
  myPrestige = 860
): RankingEntry[] {
  const copy = BASE_LEADERBOARD_ENTRIES.map(item => ({ ...item }));
  if (category === 'upset') {
    copy.sort((a, b) => b.upsetWins - a.upsetWins);
  } else if (category === 'puzzle') {
    copy.sort((a, b) => b.puzzlesCleared - a.puzzlesCleared || b.prestigeScore - a.prestigeScore);
  } else if (category === 'guild') {
    copy.sort((a, b) => b.prestigeScore - a.prestigeScore);
  } else {
    copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.map((item, idx) => {
    let scoreValue = `${item.rating.toLocaleString()} RP (승률 ${item.winRatePercent}%)`;
    if (category === 'upset') {
      scoreValue = `하극상 ${item.upsetWins}승 (승률 ${item.winRatePercent}%)`;
    } else if (category === 'puzzle') {
      scoreValue = `잔국 ${item.puzzlesCleared}/5관문 완파 · 품격 ${item.prestigeScore.toLocaleString()}점`;
    } else if (category === 'guild') {
      scoreValue = `${item.guildName} · 품격 ${item.prestigeScore.toLocaleString()}점`;
    }
    return {
      ...item,
      rank: idx + 1,
      scoreValue
    };
  });
}
